"use client";

import { useCallback, useState } from "react";

/**
 * Reads a filter value out of a `location.search` string. Anything outside
 * `options` (including an absent param) falls back to `defaultValue`, so a
 * hand-edited URL can never put the page into a filter state its chips
 * cannot represent.
 */
export function parseUrlFilter<T extends string>(
	search: string,
	key: string,
	options: readonly T[],
	defaultValue: T,
): T {
	const raw = new URLSearchParams(search).get(key);
	const match = options.find((option) => option === raw);
	return match ?? defaultValue;
}

/** Free-text URL param. Absent or whitespace becomes "". */
export function parseUrlText(search: string, key: string): string {
	return new URLSearchParams(search).get(key)?.trim() ?? "";
}

/**
 * Renders a filter back into a query string, preserving params it does not
 * own and omitting the default value so the untouched view keeps a clean URL.
 */
export function urlSearchWithFilter<T extends string>(
	currentSearch: string,
	key: string,
	value: T,
	defaultValue: T,
): string {
	const params = new URLSearchParams(currentSearch);
	if (value === defaultValue) {
		params.delete(key);
	} else {
		params.set(key, value);
	}
	return params.toString();
}

/**
 * Filter-chip state mirrored into the URL, the companion of
 * `useServerTableState`'s `syncToUrl`: a page that restores `?page=3` must
 * also restore the filter that produced it, or the restored page number is
 * applied to a different result set.
 */
export function useUrlFilter<T extends string>(
	key: string,
	options: readonly T[],
	defaultValue: T,
): [T, (value: T) => void] {
	const [value, setValue] = useState<T>(() =>
		typeof window === "undefined"
			? defaultValue
			: parseUrlFilter(window.location.search, key, options, defaultValue),
	);

	const set = useCallback(
		(next: T) => {
			setValue(next);
			if (typeof window === "undefined") return;
			const query = urlSearchWithFilter(window.location.search, key, next, defaultValue);
			const url = `${window.location.pathname}${query ? `?${query}` : ""}${window.location.hash}`;
			window.history.replaceState(window.history.state, "", url);
		},
		[key, defaultValue],
	);

	return [value, set];
}

/**
 * Free-text filter mirrored into the URL. Empty (the default) is omitted so
 * an untouched view keeps a clean query string.
 */
export function useUrlTextFilter(key: string): [string, (value: string) => void] {
	const [value, setValue] = useState(() =>
		typeof window === "undefined" ? "" : parseUrlText(window.location.search, key),
	);

	const set = useCallback(
		(next: string) => {
			setValue(next);
			if (typeof window === "undefined") return;
			const query = urlSearchWithFilter(window.location.search, key, next.trim(), "");
			const url = `${window.location.pathname}${query ? `?${query}` : ""}${window.location.hash}`;
			window.history.replaceState(window.history.state, "", url);
		},
		[key],
	);

	return [value, set];
}
